/**
 * ItemIcons - Pixel art icons for items
 * Each icon is a 10x10 grid of colors (null = transparent)
 */

type PixelGrid = (string | null)[][];

// Color palette
const C = {
  // Wood colors
  wood1: '#8B4513',
  wood2: '#A0522D',
  wood3: '#6B3A0F',
  // Stone colors
  stone1: '#808080',
  stone2: '#A0A0A0',
  stone3: '#606060',
  // Metal colors
  metal1: '#C0C0C0',
  metal2: '#D8D8D8',
  metal3: '#888888',
  iron: '#4A6670',
  // Blade colors
  blade1: '#B0B0B0',
  blade2: '#D0D0D0',
  blade3: '#707070',
  // Green/plant
  green1: '#228B22',
  green2: '#32CD32',
  green3: '#006400',
  fiber: '#9ACD32',
  // Red/meat
  red1: '#CD5C5C',
  red2: '#8B0000',
  red3: '#FF6B6B',
  // Food
  apple: '#FF4444',
  appleStem: '#654321',
  appleLeaf: '#228B22',
  meat: '#CD853F',
  cookedMeat: '#8B4513',
  // Bone
  bone1: '#F5F5DC',
  bone2: '#FFFFF0',
  bone3: '#D2B48C',
  // Purple/magic (epic spectral)
  purple1: '#9932CC',
  purple2: '#BA55D3',
  purple3: '#4B0082',
  // Orange
  orange1: '#FF8C00',
  orange2: '#FFA500',
  // Yellow
  yellow1: '#FFD700',
  yellow2: '#FFEC8B',
  // Blue
  blue1: '#4169E1',
  blue2: '#6495ED',
  blue3: '#1E90FF',
  // Black/dark
  black: '#1a1a1a',
  dark: '#333333',
  // White/light
  white: '#FFFFFF',
  light: '#F0F0F0',
  // Cloth/fiber colors
  cloth1: '#D2B48C',
  cloth2: '#C4A574',
  cloth3: '#8B7355',
  // Leather colors
  leather1: '#8B4513',
  leather2: '#A0522D',
  leather3: '#654321',
  // Wolf/fur colors
  wolfGray1: '#696969',
  wolfGray2: '#808080',
  wolfGray3: '#4A4A4A',
  wolfWhite: '#E8E8E8',
  // Demon colors (red/black, fiery)
  demon1: '#FF4444',
  demon2: '#CC0000',
  demon3: '#880000',
  demonGlow: '#FF6600',
  // Golem colors (stone with purple glow)
  golem1: '#6B6B6B',
  golem2: '#8B8B8B',
  golemGlow: '#9966FF',
  // Spectral colors (ghostly purple/cyan)
  spectral1: '#E6E6FA',
  spectral2: '#DDA0DD',
  spectral3: '#9370DB',
  spectralGlow: '#00FFFF',
  // Gun colors
  gunMetal: '#4A4A4A',
  gunMetal2: '#6A6A6A',
  gunMetal3: '#3A3A3A',
  gunBlack: '#1A1A1A',
  gunWood: '#5D4037',
  gunWood2: '#795548',
  brass: '#B5A642',
  brass2: '#D4AF37',
  // Laser/Energy colors
  laserRed: '#FF0000',
  laserCyan: '#00FFFF',
  laserGreen: '#00FF00',
  energyBlue: '#4FC3F7',
  energyBlue2: '#81D4FA',
  // Plasma colors
  plasma1: '#FF00FF',
  plasma2: '#E040FB',
  plasma3: '#AA00FF',
  // Rail colors
  railBlue: '#00BCD4',
  railGlow: '#18FFFF',
};

const _ = null; // Transparent

// ============ TOOLS ============

// Lumberjack axe - large single blade
const axeWood: PixelGrid = [
  [_, _, _, _, _, _, C.stone1, C.stone2, _, _],
  [_, _, _, _, _, C.stone1, C.stone2, C.stone1, _, _],
  [_, _, _, _, C.stone1, C.stone2, C.stone1, _, _, _],
  [_, _, _, C.stone3, C.stone1, C.stone2, _, _, _, _],
  [_, _, _, C.stone1, C.stone2, C.stone1, _, _, _, _],
  [_, _, _, C.wood1, C.stone1, _, _, _, _, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _],
  [_, C.wood1, C.wood2, _, _, _, _, _, _, _],
  [_, C.wood3, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const axeStone: PixelGrid = [
  [_, _, _, _, _, _, C.stone2, C.stone1, _, _],
  [_, _, _, _, _, C.stone2, C.stone1, C.stone2, _, _],
  [_, _, _, _, C.stone2, C.stone1, C.stone2, _, _, _],
  [_, _, _, C.stone3, C.stone2, C.stone1, _, _, _, _],
  [_, _, _, C.stone2, C.stone1, C.stone2, _, _, _, _],
  [_, _, _, C.wood1, C.stone2, _, _, _, _, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _],
  [_, C.wood1, C.wood2, _, _, _, _, _, _, _],
  [_, C.wood3, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const axeIron: PixelGrid = [
  [_, _, _, _, _, _, C.metal1, C.metal2, _, _],
  [_, _, _, _, _, C.metal1, C.metal2, C.metal1, _, _],
  [_, _, _, _, C.metal1, C.metal2, C.metal1, _, _, _],
  [_, _, _, C.metal3, C.metal1, C.metal2, _, _, _, _],
  [_, _, _, C.metal1, C.metal2, C.metal1, _, _, _, _],
  [_, _, _, C.wood1, C.metal1, _, _, _, _, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _],
  [_, C.wood1, C.wood2, _, _, _, _, _, _, _],
  [_, C.wood3, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// Pickaxe - T-shaped with horizontal head and vertical handle
const pickaxeWood: PixelGrid = [
  [_, C.stone1, C.stone3, _, _, _, _, C.stone3, C.stone1, _],
  [_, _, C.stone1, C.stone2, _, _, C.stone2, C.stone1, _, _],
  [_, _, _, C.stone3, C.stone1, C.stone1, C.stone3, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood3, C.wood1, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const pickaxeStone: PixelGrid = [
  [_, C.stone2, C.stone3, _, _, _, _, C.stone3, C.stone2, _],
  [_, _, C.stone2, C.stone1, _, _, C.stone1, C.stone2, _, _],
  [_, _, _, C.stone3, C.stone2, C.stone2, C.stone3, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood3, C.wood1, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const pickaxeIron: PixelGrid = [
  [_, C.metal1, C.metal3, _, _, _, _, C.metal3, C.metal1, _],
  [_, _, C.metal1, C.metal2, _, _, C.metal2, C.metal1, _, _],
  [_, _, _, C.metal3, C.metal1, C.metal1, C.metal3, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood3, C.wood1, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const sickleWood: PixelGrid = [
  [_, _, _, _, _, _, C.stone1, C.stone2, _, _],
  [_, _, _, _, _, C.stone1, C.stone2, _, _, _],
  [_, _, _, _, C.stone1, C.stone2, _, _, _, _],
  [_, _, _, C.stone3, C.stone1, _, _, _, _, _],
  [_, _, _, C.wood1, C.stone1, _, _, _, _, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _],
  [_, C.wood1, C.wood2, _, _, _, _, _, _, _],
  [_, C.wood3, C.wood1, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const sickleStone: PixelGrid = [
  [_, _, _, _, _, _, C.stone2, C.stone1, _, _],
  [_, _, _, _, _, C.stone2, C.stone1, _, _, _],
  [_, _, _, _, C.stone2, C.stone1, _, _, _, _],
  [_, _, _, C.stone3, C.stone2, _, _, _, _, _],
  [_, _, _, C.wood1, C.stone2, _, _, _, _, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _],
  [_, C.wood1, C.wood2, _, _, _, _, _, _, _],
  [_, C.wood3, C.wood1, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// Torch - light source
const torch: PixelGrid = [
  [_, _, _, _, C.orange2, C.yellow1, _, _, _, _],
  [_, _, _, C.orange1, C.yellow1, C.orange2, _, _, _, _],
  [_, _, _, _, C.orange1, C.yellow1, _, _, _, _],
  [_, _, _, _, C.red1, C.orange1, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood2, C.wood1, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood2, C.wood1, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood3, _, _, _, _],
  [_, _, _, _, C.wood3, _, _, _, _, _],
];

// Jackdrill - dimensional piercing drill
const jackdrill: PixelGrid = [
  [_, _, _, _, C.metal2, C.metal1, _, _, _, _],
  [_, _, _, C.metal3, C.metal1, C.metal2, C.metal3, _, _, _],
  [_, _, _, _, C.metal2, C.metal1, _, _, _, _],
  [_, _, _, C.demonGlow, C.gunMetal, C.gunMetal2, C.demonGlow, _, _, _],
  [_, _, _, C.gunMetal3, C.gunMetal, C.gunMetal2, C.gunMetal3, _, _, _],
  [_, _, _, C.demon1, C.gunMetal2, C.gunMetal, C.demon1, _, _, _],
  [_, _, _, C.gunMetal, C.gunMetal2, C.gunMetal, C.gunMetal, _, _, _],
  [_, _, _, _, C.gunMetal3, C.gunMetal, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, C.wood3, C.wood1, _, _, _, _],
];

// ============ WEAPONS ============

const swordWood: PixelGrid = [
  [_, _, _, _, _, _, _, C.stone1, C.stone2, _],
  [_, _, _, _, _, _, C.stone1, C.stone2, _, _],
  [_, _, _, _, _, C.stone1, C.stone2, _, _, _],
  [_, _, _, _, C.stone1, C.stone2, _, _, _, _],
  [_, _, _, C.stone3, C.stone1, _, _, _, _, _],
  [_, _, C.wood2, C.wood1, _, _, _, _, _, _],
  [_, C.wood1, C.wood2, _, _, _, _, _, _, _],
  [_, C.wood2, C.wood1, _, _, _, _, _, _, _],
  [_, _, C.wood3, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const swordStone: PixelGrid = [
  [_, _, _, _, _, _, _, C.blade1, C.blade2, _],
  [_, _, _, _, _, _, C.blade1, C.blade2, _, _],
  [_, _, _, _, _, C.blade1, C.blade2, _, _, _],
  [_, _, _, _, C.blade1, C.blade2, _, _, _, _],
  [_, _, _, C.blade3, C.blade1, _, _, _, _, _],
  [_, _, C.wood2, C.wood1, _, _, _, _, _, _],
  [_, C.wood1, C.wood2, _, _, _, _, _, _, _],
  [_, C.wood2, C.wood1, _, _, _, _, _, _, _],
  [_, _, C.wood3, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const swordIron: PixelGrid = [
  [_, _, _, _, _, _, _, C.metal1, C.metal2, _],
  [_, _, _, _, _, _, C.metal1, C.metal2, _, _],
  [_, _, _, _, _, C.metal1, C.metal2, _, _, _],
  [_, _, _, _, C.metal1, C.metal2, _, _, _, _],
  [_, _, _, C.metal3, C.metal1, _, _, _, _, _],
  [_, _, C.yellow1, C.wood1, _, _, _, _, _, _],
  [_, C.wood1, C.yellow1, _, _, _, _, _, _, _],
  [_, C.wood2, C.wood1, _, _, _, _, _, _, _],
  [_, _, C.wood3, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const spearWood: PixelGrid = [
  [_, _, _, _, _, _, _, _, C.stone1, _],
  [_, _, _, _, _, _, _, C.stone1, C.stone2, _],
  [_, _, _, _, _, _, C.stone1, C.stone2, _, _],
  [_, _, _, _, _, C.wood1, C.stone1, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, C.wood1, C.wood2, _, _, _, _, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _],
  [_, C.wood1, C.wood2, _, _, _, _, _, _, _],
  [_, C.wood3, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const spearStone: PixelGrid = [
  [_, _, _, _, _, _, _, _, C.blade1, _],
  [_, _, _, _, _, _, _, C.blade1, C.blade2, _],
  [_, _, _, _, _, _, C.blade1, C.blade2, _, _],
  [_, _, _, _, _, C.wood1, C.blade1, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, C.wood1, C.wood2, _, _, _, _, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _],
  [_, C.wood1, C.wood2, _, _, _, _, _, _, _],
  [_, C.wood3, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const boneKnife: PixelGrid = [
  [_, _, _, _, _, _, _, C.bone1, C.bone2, _],
  [_, _, _, _, _, _, C.bone1, C.bone2, _, _],
  [_, _, _, _, _, C.bone1, C.bone2, _, _, _],
  [_, _, _, _, C.bone3, C.bone1, _, _, _, _],
  [_, _, _, C.bone3, C.bone1, _, _, _, _, _],
  [_, _, C.wood2, C.wood1, _, _, _, _, _, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const boneSpear: PixelGrid = [
  [_, _, _, _, _, _, _, _, C.bone1, _],
  [_, _, _, _, _, _, _, C.bone1, C.bone2, _],
  [_, _, _, _, _, _, C.bone1, C.bone2, _, _],
  [_, _, _, _, _, C.bone3, C.bone1, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, C.wood1, C.wood2, _, _, _, _, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _],
  [_, C.wood1, C.wood2, _, _, _, _, _, _, _],
  [_, C.wood3, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const golemSword: PixelGrid = [
  [_, _, _, _, _, _, _, C.purple1, C.purple2, _],
  [_, _, _, _, _, _, C.purple1, C.purple2, _, _],
  [_, _, _, _, _, C.purple1, C.purple2, _, _, _],
  [_, _, _, _, C.purple1, C.purple2, _, _, _, _],
  [_, _, _, C.purple3, C.purple1, _, _, _, _, _],
  [_, _, C.yellow1, C.stone1, _, _, _, _, _, _],
  [_, C.stone1, C.yellow1, _, _, _, _, _, _, _],
  [_, C.stone2, C.stone1, _, _, _, _, _, _, _],
  [_, _, C.stone3, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// Wolf Fang Dagger - swift dagger with fang tip
const wolfFangDagger: PixelGrid = [
  [_, _, _, _, _, _, _, C.bone1, C.bone2, _],
  [_, _, _, _, _, _, C.bone1, C.bone2, _, _],
  [_, _, _, _, _, C.bone1, C.bone2, _, _, _],
  [_, _, _, _, C.bone3, C.bone1, _, _, _, _],
  [_, _, _, C.stone2, C.bone3, _, _, _, _, _],
  [_, _, C.stone1, C.stone2, _, _, _, _, _, _],
  [_, _, C.stone2, C.stone1, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// Bone Club - heavy blunt weapon
const boneClub: PixelGrid = [
  [_, _, _, _, _, C.bone2, C.bone1, C.bone2, _, _],
  [_, _, _, _, C.bone1, C.bone2, C.bone1, C.bone2, _, _],
  [_, _, _, _, C.bone2, C.bone1, C.bone2, C.bone1, _, _],
  [_, _, _, _, C.bone1, C.bone3, C.bone1, _, _, _],
  [_, _, _, _, C.bone3, C.bone1, _, _, _, _],
  [_, _, _, C.bone1, C.bone2, _, _, _, _, _],
  [_, _, C.bone1, C.bone2, _, _, _, _, _, _],
  [_, C.bone3, C.bone1, _, _, _, _, _, _, _],
  [_, C.bone2, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// Orc Cleaver - brutal axe-like weapon
const orcCleaver: PixelGrid = [
  [_, _, _, _, C.metal3, C.metal1, C.metal2, _, _, _],
  [_, _, _, C.metal3, C.metal1, C.metal2, C.metal1, _, _, _],
  [_, _, C.metal3, C.metal1, C.metal2, C.metal1, _, _, _, _],
  [_, _, _, C.metal3, C.metal1, C.metal2, _, _, _, _],
  [_, _, _, _, C.wood1, C.metal1, _, _, _, _],
  [_, _, _, C.wood1, C.wood2, _, _, _, _, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _],
  [_, C.wood1, C.wood2, _, _, _, _, _, _, _],
  [_, C.wood3, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// Golem Hammer - massive hammer
const golemHammer: PixelGrid = [
  [_, _, C.stone1, C.stone2, C.purple1, C.stone2, C.stone1, _, _, _],
  [_, _, C.stone2, C.purple1, C.purple2, C.purple1, C.stone2, _, _, _],
  [_, _, C.stone1, C.stone2, C.purple1, C.stone2, C.stone1, _, _, _],
  [_, _, _, _, C.stone1, C.stone2, _, _, _, _],
  [_, _, _, C.wood1, C.wood2, _, _, _, _, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _],
  [_, C.wood1, C.wood2, _, _, _, _, _, _, _],
  [_, C.wood3, C.wood1, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// Demon Blade - fiery cursed sword
const demonBlade: PixelGrid = [
  [_, _, _, _, _, _, _, C.red1, C.orange1, _],
  [_, _, _, _, _, _, C.red1, C.orange2, _, _],
  [_, _, _, _, _, C.red1, C.orange1, _, _, _],
  [_, _, _, _, C.red2, C.red1, _, _, _, _],
  [_, _, _, C.red2, C.red1, _, _, _, _, _],
  [_, _, C.dark, C.red2, _, _, _, _, _, _],
  [_, C.dark, C.red2, _, _, _, _, _, _, _],
  [_, C.black, C.dark, _, _, _, _, _, _, _],
  [_, _, C.dark, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// Spectral Scythe - ghostly weapon
const spectralScythe: PixelGrid = [
  [_, _, C.purple2, C.purple1, C.purple2, _, _, _, _, _],
  [_, C.purple1, C.purple2, C.white, C.purple1, _, _, _, _, _],
  [_, _, C.purple1, C.purple2, C.purple1, _, _, _, _, _],
  [_, _, _, _, C.purple2, C.purple1, _, _, _, _],
  [_, _, _, _, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, C.wood1, C.wood2, _, _, _, _, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _],
  [_, C.wood1, C.wood2, _, _, _, _, _, _, _],
  [_, C.wood3, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// Spider Fang - venomous dagger
const spiderFang: PixelGrid = [
  [_, _, _, _, _, _, _, C.green1, C.green2, _],
  [_, _, _, _, _, _, C.dark, C.green1, _, _],
  [_, _, _, _, _, C.dark, C.green2, _, _, _],
  [_, _, _, _, C.dark, C.green1, _, _, _, _],
  [_, _, _, C.black, C.dark, _, _, _, _, _],
  [_, _, C.red2, C.dark, _, _, _, _, _, _],
  [_, _, C.dark, C.red2, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// Demon Heart - pulsing dark heart
const demonHeart: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.red2, C.red1, _, C.red1, C.red2, _, _, _],
  [_, C.red1, C.red3, C.dark, C.red1, C.dark, C.red3, C.red1, _, _],
  [_, C.red2, C.dark, C.red1, C.dark, C.red1, C.dark, C.red2, _, _],
  [_, C.red1, C.red3, C.dark, C.red1, C.dark, C.red3, C.red1, _, _],
  [_, _, C.red2, C.red1, C.dark, C.red1, C.red2, _, _, _],
  [_, _, _, C.red1, C.red2, C.red1, _, _, _, _],
  [_, _, _, _, C.red2, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// ============ RESOURCES ============

const wood: PixelGrid = [
  [_, _, C.wood2, C.wood1, C.wood2, C.wood1, _, _, _, _],
  [_, _, C.wood1, C.wood2, C.wood1, C.wood2, _, _, _, _],
  [_, _, C.wood2, C.wood1, C.wood2, C.wood1, _, _, _, _],
  [_, _, C.wood1, C.wood3, C.wood1, C.wood2, _, _, _, _],
  [_, _, C.wood2, C.wood1, C.wood2, C.wood1, _, _, _, _],
  [_, _, C.wood1, C.wood2, C.wood1, C.wood2, _, _, _, _],
  [_, _, C.wood2, C.wood1, C.wood2, C.wood1, _, _, _, _],
  [_, _, C.wood1, C.wood3, C.wood1, C.wood2, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const stone: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, C.stone2, C.stone1, C.stone2, _, _, _, _],
  [_, _, C.stone1, C.stone2, C.stone1, C.stone2, C.stone1, _, _, _],
  [_, C.stone2, C.stone1, C.stone3, C.stone2, C.stone1, C.stone2, _, _, _],
  [_, C.stone1, C.stone2, C.stone1, C.stone3, C.stone2, C.stone1, _, _, _],
  [_, C.stone2, C.stone3, C.stone2, C.stone1, C.stone3, C.stone2, _, _, _],
  [_, _, C.stone1, C.stone2, C.stone3, C.stone2, C.stone1, _, _, _],
  [_, _, _, C.stone2, C.stone1, C.stone2, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const fiber: PixelGrid = [
  [_, _, _, _, C.fiber, _, _, _, _, _],
  [_, _, _, C.fiber, C.green1, C.fiber, _, _, _, _],
  [_, _, C.fiber, _, C.fiber, _, C.fiber, _, _, _],
  [_, _, C.green1, C.fiber, C.green1, C.fiber, _, _, _, _],
  [_, _, _, C.fiber, _, C.fiber, _, _, _, _],
  [_, _, C.fiber, C.green1, C.fiber, C.green1, _, _, _, _],
  [_, _, _, C.fiber, _, C.fiber, _, _, _, _],
  [_, _, _, _, C.fiber, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const ironOre: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, C.stone2, C.iron, C.stone2, _, _, _, _],
  [_, _, C.iron, C.stone1, C.iron, C.stone1, C.iron, _, _, _],
  [_, C.stone2, C.iron, C.metal1, C.iron, C.metal1, C.stone2, _, _, _],
  [_, C.iron, C.stone1, C.iron, C.metal2, C.iron, C.stone1, _, _, _],
  [_, C.stone2, C.iron, C.metal1, C.iron, C.metal1, C.stone2, _, _, _],
  [_, _, C.iron, C.stone1, C.iron, C.stone1, C.iron, _, _, _],
  [_, _, _, C.stone2, C.iron, C.stone2, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const coal: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, C.dark, C.black, C.dark, _, _, _, _],
  [_, _, C.black, C.dark, C.black, C.dark, C.black, _, _, _],
  [_, C.dark, C.black, C.dark, C.black, C.dark, C.black, _, _, _],
  [_, C.black, C.dark, C.black, C.dark, C.black, C.dark, _, _, _],
  [_, C.dark, C.black, C.dark, C.black, C.dark, C.black, _, _, _],
  [_, _, C.black, C.dark, C.black, C.dark, C.black, _, _, _],
  [_, _, _, C.dark, C.black, C.dark, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// ============ FOOD ============

const apple: PixelGrid = [
  [_, _, _, _, C.appleStem, _, _, _, _, _],
  [_, _, _, C.appleLeaf, C.appleStem, _, _, _, _, _],
  [_, _, C.apple, C.apple, C.red3, C.apple, C.apple, _, _, _],
  [_, C.apple, C.red3, C.apple, C.apple, C.apple, C.apple, _, _, _],
  [_, C.apple, C.apple, C.apple, C.apple, C.red1, C.apple, _, _, _],
  [_, C.apple, C.apple, C.red1, C.apple, C.apple, C.apple, _, _, _],
  [_, C.apple, C.apple, C.apple, C.apple, C.apple, C.apple, _, _, _],
  [_, _, C.red2, C.apple, C.apple, C.red2, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const rawMeat: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.red1, C.red3, C.red1, C.red3, _, _, _, _],
  [_, C.red3, C.red1, C.meat, C.red1, C.red3, C.red1, _, _, _],
  [_, C.red1, C.meat, C.red1, C.meat, C.red1, C.red3, _, _, _],
  [_, C.red3, C.red1, C.meat, C.red1, C.meat, C.red1, _, _, _],
  [_, C.red1, C.red3, C.red1, C.red3, C.red1, C.red3, _, _, _],
  [_, _, C.red1, C.red3, C.red1, C.red3, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const cookedMeat: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.cookedMeat, C.wood1, C.cookedMeat, C.wood1, _, _, _, _],
  [_, C.wood1, C.cookedMeat, C.meat, C.cookedMeat, C.wood1, C.cookedMeat, _, _, _],
  [_, C.cookedMeat, C.meat, C.cookedMeat, C.meat, C.cookedMeat, C.wood1, _, _, _],
  [_, C.wood1, C.cookedMeat, C.meat, C.cookedMeat, C.meat, C.cookedMeat, _, _, _],
  [_, C.cookedMeat, C.wood1, C.cookedMeat, C.wood1, C.cookedMeat, C.wood1, _, _, _],
  [_, _, C.cookedMeat, C.wood1, C.cookedMeat, C.wood1, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// ============ MONSTER DROPS ============

const bone: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, C.bone1, C.bone2, _, _, _, _, _, _, _],
  [_, C.bone2, C.bone1, C.bone2, _, _, _, _, _, _],
  [_, _, C.bone1, C.bone2, _, _, _, _, _, _],
  [_, _, _, C.bone1, C.bone2, _, _, _, _, _],
  [_, _, _, _, C.bone1, C.bone2, _, _, _, _],
  [_, _, _, _, _, C.bone1, C.bone2, _, _, _],
  [_, _, _, _, _, C.bone2, C.bone1, C.bone2, _, _],
  [_, _, _, _, _, _, C.bone1, C.bone2, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const ectoplasm: PixelGrid = [
  [_, _, _, _, C.purple2, _, _, _, _, _],
  [_, _, _, C.purple2, C.purple1, C.purple2, _, _, _, _],
  [_, _, C.purple1, C.purple2, C.white, C.purple1, _, _, _, _],
  [_, C.purple2, C.purple1, C.white, C.purple2, C.purple1, _, _, _, _],
  [_, C.purple1, C.purple2, C.purple1, C.white, C.purple2, _, _, _, _],
  [_, _, C.purple1, C.purple2, C.purple1, C.purple2, _, _, _, _],
  [_, _, _, C.purple2, C.purple1, _, _, _, _, _],
  [_, _, _, _, C.purple2, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const spiderEye: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, C.red2, C.red2, C.red2, _, _, _, _],
  [_, _, C.red2, C.red1, C.red1, C.red1, C.red2, _, _, _],
  [_, C.red2, C.red1, C.black, C.black, C.red1, C.red2, _, _, _],
  [_, C.red2, C.red1, C.black, C.white, C.red1, C.red2, _, _, _],
  [_, C.red2, C.red1, C.red1, C.red1, C.red1, C.red2, _, _, _],
  [_, _, C.red2, C.red1, C.red1, C.red2, _, _, _, _],
  [_, _, _, C.red2, C.red2, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const wolfPelt: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.stone2, C.stone1, C.stone2, C.stone1, _, _, _, _],
  [_, C.stone1, C.stone2, C.stone1, C.stone2, C.stone1, C.stone2, _, _, _],
  [_, C.stone2, C.stone1, C.light, C.stone1, C.light, C.stone1, _, _, _],
  [_, C.stone1, C.light, C.stone1, C.light, C.stone1, C.stone2, _, _, _],
  [_, C.stone2, C.stone1, C.stone2, C.stone1, C.stone2, C.stone1, _, _, _],
  [_, _, C.stone1, C.stone2, C.stone1, C.stone2, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const golemCore: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, C.purple3, C.purple1, C.purple3, _, _, _, _],
  [_, _, C.purple1, C.purple2, C.yellow1, C.purple2, C.purple1, _, _, _],
  [_, C.purple3, C.purple2, C.yellow1, C.yellow2, C.yellow1, C.purple3, _, _, _],
  [_, C.purple1, C.yellow1, C.yellow2, C.white, C.yellow2, C.purple1, _, _, _],
  [_, C.purple3, C.purple2, C.yellow1, C.yellow2, C.yellow1, C.purple3, _, _, _],
  [_, _, C.purple1, C.purple2, C.yellow1, C.purple2, C.purple1, _, _, _],
  [_, _, _, C.purple3, C.purple1, C.purple3, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// ============ CONSUMABLES ============

const bandage: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.light, C.white, C.light, C.white, _, _, _, _],
  [_, C.light, C.white, C.light, C.white, C.light, _, _, _, _],
  [_, C.white, C.light, C.red1, C.light, C.white, _, _, _, _],
  [_, C.light, C.white, C.light, C.white, C.light, _, _, _, _],
  [_, C.white, C.light, C.white, C.light, C.white, _, _, _, _],
  [_, _, C.light, C.white, C.light, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const healthPotion: PixelGrid = [
  [_, _, _, _, C.stone1, _, _, _, _, _],
  [_, _, _, C.stone1, C.stone2, C.stone1, _, _, _, _],
  [_, _, _, C.stone2, C.stone1, C.stone2, _, _, _, _],
  [_, _, C.red1, C.red3, C.red1, C.red3, C.red1, _, _, _],
  [_, _, C.red3, C.red1, C.white, C.red1, C.red3, _, _, _],
  [_, _, C.red1, C.red3, C.red1, C.red3, C.red1, _, _, _],
  [_, _, C.red3, C.red1, C.red3, C.red1, C.red3, _, _, _],
  [_, _, _, C.red1, C.red3, C.red1, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const antidote: PixelGrid = [
  [_, _, _, _, C.stone1, _, _, _, _, _],
  [_, _, _, C.stone1, C.stone2, C.stone1, _, _, _, _],
  [_, _, _, C.stone2, C.stone1, C.stone2, _, _, _, _],
  [_, _, C.green1, C.green2, C.green1, C.green2, C.green1, _, _, _],
  [_, _, C.green2, C.green1, C.white, C.green1, C.green2, _, _, _],
  [_, _, C.green1, C.green2, C.green1, C.green2, C.green1, _, _, _],
  [_, _, C.green2, C.green1, C.green2, C.green1, C.green2, _, _, _],
  [_, _, _, C.green1, C.green2, C.green1, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const furCloak: PixelGrid = [
  [_, _, _, C.stone1, _, C.stone1, _, _, _, _],
  [_, _, C.stone2, C.stone1, C.stone2, C.stone1, C.stone2, _, _, _],
  [_, C.stone1, C.light, C.stone2, C.light, C.stone2, C.light, C.stone1, _, _],
  [_, C.stone2, C.light, C.stone1, C.light, C.stone1, C.light, C.stone2, _, _],
  [_, C.stone1, C.stone2, C.light, C.stone2, C.light, C.stone2, C.stone1, _, _],
  [_, _, C.stone1, C.stone2, C.stone1, C.stone2, C.stone1, _, _, _],
  [_, _, _, C.stone2, C.stone1, C.stone2, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const spiritEssence: PixelGrid = [
  [_, _, _, _, C.purple2, _, _, _, _, _],
  [_, _, _, C.purple2, C.white, C.purple2, _, _, _, _],
  [_, _, C.purple1, C.white, C.purple2, C.white, C.purple1, _, _, _],
  [_, C.purple2, C.white, C.purple1, C.white, C.purple1, C.white, _, _, _],
  [_, C.purple1, C.purple2, C.white, C.purple2, C.white, C.purple2, _, _, _],
  [_, _, C.purple1, C.purple2, C.white, C.purple2, C.purple1, _, _, _],
  [_, _, _, C.purple2, C.purple1, C.purple2, _, _, _, _],
  [_, _, _, _, C.purple2, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// ============ REGULAR TIER - CLOTH ARMOR ============

const clothHood: PixelGrid = [
  [_, _, _, C.cloth1, C.cloth2, C.cloth1, _, _, _, _],
  [_, _, C.cloth2, C.cloth1, C.cloth2, C.cloth1, C.cloth2, _, _, _],
  [_, C.cloth1, C.cloth2, C.cloth3, C.cloth3, C.cloth3, C.cloth2, C.cloth1, _, _],
  [_, C.cloth2, C.cloth3, _, _, _, C.cloth3, C.cloth2, _, _],
  [_, C.cloth1, C.cloth3, _, _, _, C.cloth3, C.cloth1, _, _],
  [_, _, C.cloth2, C.cloth3, _, C.cloth3, C.cloth2, _, _, _],
  [_, _, _, C.cloth1, C.cloth2, C.cloth1, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const clothShirt: PixelGrid = [
  [_, _, _, C.cloth1, C.cloth2, C.cloth1, _, _, _, _],
  [_, _, C.cloth2, C.cloth1, C.cloth2, C.cloth1, C.cloth2, _, _, _],
  [_, C.cloth1, C.cloth2, C.cloth1, C.cloth2, C.cloth1, C.cloth2, C.cloth1, _, _],
  [_, C.cloth2, C.cloth1, C.cloth2, C.cloth1, C.cloth2, C.cloth1, C.cloth2, _, _],
  [_, _, C.cloth1, C.cloth2, C.cloth1, C.cloth2, C.cloth1, _, _, _],
  [_, _, C.cloth2, C.cloth1, C.cloth2, C.cloth1, C.cloth2, _, _, _],
  [_, _, C.cloth1, C.cloth2, C.cloth1, C.cloth2, C.cloth1, _, _, _],
  [_, _, _, C.cloth2, C.cloth1, C.cloth2, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const clothPants: PixelGrid = [
  [_, _, _, C.cloth1, C.cloth2, C.cloth1, _, _, _, _],
  [_, _, C.cloth2, C.cloth1, C.cloth2, C.cloth1, C.cloth2, _, _, _],
  [_, _, C.cloth1, C.cloth2, C.cloth1, C.cloth2, C.cloth1, _, _, _],
  [_, _, C.cloth2, C.cloth1, _, C.cloth1, C.cloth2, _, _, _],
  [_, _, C.cloth1, C.cloth2, _, C.cloth2, C.cloth1, _, _, _],
  [_, _, C.cloth2, C.cloth1, _, C.cloth1, C.cloth2, _, _, _],
  [_, _, C.cloth1, C.cloth2, _, C.cloth2, C.cloth1, _, _, _],
  [_, _, C.cloth3, C.cloth1, _, C.cloth1, C.cloth3, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const clothSandals: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.cloth1, C.cloth2, C.cloth1, C.cloth2, C.cloth1, _, _, _],
  [_, C.cloth2, C.cloth3, C.cloth1, _, C.cloth1, C.cloth3, C.cloth2, _, _],
  [_, C.cloth1, C.cloth2, C.cloth1, _, C.cloth1, C.cloth2, C.cloth1, _, _],
  [_, C.cloth3, C.cloth1, C.cloth2, _, C.cloth2, C.cloth1, C.cloth3, _, _],
  [_, _, C.cloth2, C.cloth1, _, C.cloth1, C.cloth2, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const clothWraps: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.cloth1, C.cloth2, _, C.cloth2, C.cloth1, _, _, _],
  [_, C.cloth2, C.cloth1, C.cloth2, _, C.cloth2, C.cloth1, C.cloth2, _, _],
  [_, C.cloth1, C.cloth2, C.cloth1, _, C.cloth1, C.cloth2, C.cloth1, _, _],
  [_, C.cloth2, C.cloth1, C.cloth2, _, C.cloth2, C.cloth1, C.cloth2, _, _],
  [_, _, C.cloth2, C.cloth1, _, C.cloth1, C.cloth2, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// ============ REGULAR TIER - LEATHER ARMOR ============

const leatherCap: PixelGrid = [
  [_, _, _, C.leather1, C.leather2, C.leather1, _, _, _, _],
  [_, _, C.leather2, C.leather1, C.leather2, C.leather1, C.leather2, _, _, _],
  [_, C.leather1, C.leather2, C.leather3, C.leather3, C.leather3, C.leather2, C.leather1, _, _],
  [_, C.leather2, C.leather3, C.leather1, C.leather2, C.leather1, C.leather3, C.leather2, _, _],
  [_, _, C.leather1, C.leather2, C.leather1, C.leather2, C.leather1, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const leatherTunic: PixelGrid = [
  [_, _, _, C.leather1, C.leather2, C.leather1, _, _, _, _],
  [_, _, C.leather2, C.leather1, C.leather2, C.leather1, C.leather2, _, _, _],
  [_, C.leather1, C.leather2, C.leather1, C.leather2, C.leather1, C.leather2, C.leather1, _, _],
  [_, C.leather2, C.leather1, C.leather3, C.leather1, C.leather3, C.leather1, C.leather2, _, _],
  [_, _, C.leather1, C.leather2, C.leather1, C.leather2, C.leather1, _, _, _],
  [_, _, C.leather2, C.leather1, C.leather2, C.leather1, C.leather2, _, _, _],
  [_, _, C.leather1, C.leather2, C.leather1, C.leather2, C.leather1, _, _, _],
  [_, _, _, C.leather3, C.leather1, C.leather3, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const leatherPants: PixelGrid = [
  [_, _, _, C.leather1, C.leather2, C.leather1, _, _, _, _],
  [_, _, C.leather2, C.leather1, C.leather2, C.leather1, C.leather2, _, _, _],
  [_, _, C.leather1, C.leather3, C.leather1, C.leather3, C.leather1, _, _, _],
  [_, _, C.leather2, C.leather1, _, C.leather1, C.leather2, _, _, _],
  [_, _, C.leather1, C.leather2, _, C.leather2, C.leather1, _, _, _],
  [_, _, C.leather2, C.leather1, _, C.leather1, C.leather2, _, _, _],
  [_, _, C.leather1, C.leather2, _, C.leather2, C.leather1, _, _, _],
  [_, _, C.leather3, C.leather1, _, C.leather1, C.leather3, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const leatherBoots: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.leather1, C.leather2, _, C.leather2, C.leather1, _, _, _],
  [_, C.leather2, C.leather1, C.leather2, _, C.leather2, C.leather1, C.leather2, _, _],
  [_, C.leather1, C.leather3, C.leather1, _, C.leather1, C.leather3, C.leather1, _, _],
  [_, C.leather2, C.leather1, C.leather2, _, C.leather2, C.leather1, C.leather2, _, _],
  [_, C.leather3, C.leather2, C.leather1, _, C.leather1, C.leather2, C.leather3, _, _],
  [_, _, C.leather1, C.leather2, _, C.leather2, C.leather1, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const leatherGloves: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.leather1, C.leather2, _, C.leather2, C.leather1, _, _, _],
  [_, C.leather2, C.leather1, C.leather2, _, C.leather2, C.leather1, C.leather2, _, _],
  [_, C.leather1, C.leather3, C.leather1, _, C.leather1, C.leather3, C.leather1, _, _],
  [_, C.leather2, C.leather1, C.leather2, _, C.leather2, C.leather1, C.leather2, _, _],
  [_, _, C.leather2, C.leather1, _, C.leather1, C.leather2, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// ============ MAGIC TIER - IRON ARMOR ============

const ironHelm: PixelGrid = [
  [_, _, _, C.metal1, C.metal2, C.metal1, _, _, _, _],
  [_, _, C.metal2, C.metal1, C.metal2, C.metal1, C.metal2, _, _, _],
  [_, C.metal1, C.metal2, C.metal3, C.metal3, C.metal3, C.metal2, C.metal1, _, _],
  [_, C.metal2, C.metal3, C.dark, C.dark, C.dark, C.metal3, C.metal2, _, _],
  [_, C.metal1, C.metal3, C.dark, _, C.dark, C.metal3, C.metal1, _, _],
  [_, _, C.metal2, C.metal3, C.metal1, C.metal3, C.metal2, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const ironChestplate: PixelGrid = [
  [_, _, _, C.metal1, C.metal2, C.metal1, _, _, _, _],
  [_, _, C.metal2, C.metal1, C.metal2, C.metal1, C.metal2, _, _, _],
  [_, C.metal1, C.metal2, C.metal1, C.metal2, C.metal1, C.metal2, C.metal1, _, _],
  [_, C.metal2, C.metal1, C.metal3, C.metal1, C.metal3, C.metal1, C.metal2, _, _],
  [_, C.metal1, C.metal2, C.metal1, C.metal2, C.metal1, C.metal2, C.metal1, _, _],
  [_, _, C.metal1, C.metal2, C.metal1, C.metal2, C.metal1, _, _, _],
  [_, _, C.metal2, C.metal1, C.metal2, C.metal1, C.metal2, _, _, _],
  [_, _, _, C.metal3, C.metal1, C.metal3, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const ironGreaves: PixelGrid = [
  [_, _, _, C.metal1, C.metal2, C.metal1, _, _, _, _],
  [_, _, C.metal2, C.metal1, C.metal2, C.metal1, C.metal2, _, _, _],
  [_, _, C.metal1, C.metal3, C.metal1, C.metal3, C.metal1, _, _, _],
  [_, _, C.metal2, C.metal1, _, C.metal1, C.metal2, _, _, _],
  [_, _, C.metal1, C.metal2, _, C.metal2, C.metal1, _, _, _],
  [_, _, C.metal2, C.metal1, _, C.metal1, C.metal2, _, _, _],
  [_, _, C.metal1, C.metal2, _, C.metal2, C.metal1, _, _, _],
  [_, _, C.metal3, C.metal1, _, C.metal1, C.metal3, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const ironBoots: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.metal1, C.metal2, _, C.metal2, C.metal1, _, _, _],
  [_, C.metal2, C.metal1, C.metal2, _, C.metal2, C.metal1, C.metal2, _, _],
  [_, C.metal1, C.metal3, C.metal1, _, C.metal1, C.metal3, C.metal1, _, _],
  [_, C.metal2, C.metal1, C.metal2, _, C.metal2, C.metal1, C.metal2, _, _],
  [_, C.metal3, C.metal2, C.metal1, _, C.metal1, C.metal2, C.metal3, _, _],
  [_, _, C.metal1, C.metal2, _, C.metal2, C.metal1, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// ============ MAGIC TIER - BONE ARMOR ============

const boneHelm: PixelGrid = [
  [_, _, C.bone1, C.bone2, _, C.bone2, C.bone1, _, _, _],
  [_, C.bone2, C.bone1, C.bone2, C.bone1, C.bone2, C.bone1, C.bone2, _, _],
  [_, C.bone1, C.bone2, C.bone3, C.bone3, C.bone3, C.bone2, C.bone1, _, _],
  [_, C.bone2, C.bone3, C.dark, C.dark, C.dark, C.bone3, C.bone2, _, _],
  [_, C.bone1, C.bone3, C.dark, _, C.dark, C.bone3, C.bone1, _, _],
  [_, _, C.bone2, C.bone3, C.bone1, C.bone3, C.bone2, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const boneArmor: PixelGrid = [
  [_, _, C.bone1, _, C.bone2, _, C.bone1, _, _, _],
  [_, C.bone2, C.bone1, C.bone2, C.bone1, C.bone2, C.bone1, C.bone2, _, _],
  [_, C.bone1, C.bone2, C.bone1, C.bone2, C.bone1, C.bone2, C.bone1, _, _],
  [_, C.bone2, C.bone1, C.bone3, C.bone1, C.bone3, C.bone1, C.bone2, _, _],
  [_, C.bone1, C.bone2, C.bone1, C.bone2, C.bone1, C.bone2, C.bone1, _, _],
  [_, _, C.bone1, C.bone2, C.bone1, C.bone2, C.bone1, _, _, _],
  [_, _, C.bone2, C.bone1, C.bone2, C.bone1, C.bone2, _, _, _],
  [_, _, _, C.bone3, C.bone1, C.bone3, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const boneGreaves: PixelGrid = [
  [_, _, _, C.bone1, C.bone2, C.bone1, _, _, _, _],
  [_, _, C.bone2, C.bone1, C.bone2, C.bone1, C.bone2, _, _, _],
  [_, _, C.bone1, C.bone3, C.bone1, C.bone3, C.bone1, _, _, _],
  [_, _, C.bone2, C.bone1, _, C.bone1, C.bone2, _, _, _],
  [_, _, C.bone1, C.bone2, _, C.bone2, C.bone1, _, _, _],
  [_, _, C.bone2, C.bone1, _, C.bone1, C.bone2, _, _, _],
  [_, _, C.bone1, C.bone2, _, C.bone2, C.bone1, _, _, _],
  [_, _, C.bone3, C.bone1, _, C.bone1, C.bone3, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const boneBoots: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.bone1, C.bone2, _, C.bone2, C.bone1, _, _, _],
  [_, C.bone2, C.bone1, C.bone2, _, C.bone2, C.bone1, C.bone2, _, _],
  [_, C.bone1, C.bone3, C.bone1, _, C.bone1, C.bone3, C.bone1, _, _],
  [_, C.bone2, C.bone1, C.bone2, _, C.bone2, C.bone1, C.bone2, _, _],
  [_, C.bone3, C.bone2, C.bone1, _, C.bone1, C.bone2, C.bone3, _, _],
  [_, _, C.bone1, C.bone2, _, C.bone2, C.bone1, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// ============ MAGIC TIER - WOLF ARMOR ============

const wolfHood: PixelGrid = [
  [_, C.wolfGray1, _, _, _, _, _, C.wolfGray1, _, _],
  [_, C.wolfGray2, C.wolfGray1, C.wolfWhite, _, C.wolfWhite, C.wolfGray1, C.wolfGray2, _, _],
  [_, _, C.wolfGray2, C.wolfGray1, C.wolfWhite, C.wolfGray1, C.wolfGray2, _, _, _],
  [_, C.wolfGray1, C.wolfGray2, C.wolfGray3, C.wolfGray3, C.wolfGray3, C.wolfGray2, C.wolfGray1, _, _],
  [_, C.wolfGray2, C.wolfGray3, C.dark, _, C.dark, C.wolfGray3, C.wolfGray2, _, _],
  [_, _, C.wolfGray2, C.wolfGray3, C.dark, C.wolfGray3, C.wolfGray2, _, _, _],
  [_, _, _, C.wolfGray1, C.wolfGray2, C.wolfGray1, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const wolfPeltCloak: PixelGrid = [
  [_, _, _, C.wolfGray1, _, C.wolfGray1, _, _, _, _],
  [_, _, C.wolfGray2, C.wolfGray1, C.wolfWhite, C.wolfGray1, C.wolfGray2, _, _, _],
  [_, C.wolfGray1, C.wolfWhite, C.wolfGray2, C.wolfWhite, C.wolfGray2, C.wolfWhite, C.wolfGray1, _, _],
  [_, C.wolfGray2, C.wolfWhite, C.wolfGray1, C.wolfWhite, C.wolfGray1, C.wolfWhite, C.wolfGray2, _, _],
  [_, C.wolfGray1, C.wolfGray2, C.wolfWhite, C.wolfGray2, C.wolfWhite, C.wolfGray2, C.wolfGray1, _, _],
  [_, _, C.wolfGray1, C.wolfGray2, C.wolfGray1, C.wolfGray2, C.wolfGray1, _, _, _],
  [_, _, _, C.wolfGray2, C.wolfGray1, C.wolfGray2, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const wolfPants: PixelGrid = [
  [_, _, _, C.wolfGray1, C.wolfGray2, C.wolfGray1, _, _, _, _],
  [_, _, C.wolfGray2, C.wolfGray1, C.wolfWhite, C.wolfGray1, C.wolfGray2, _, _, _],
  [_, _, C.wolfGray1, C.wolfGray3, C.wolfGray1, C.wolfGray3, C.wolfGray1, _, _, _],
  [_, _, C.wolfGray2, C.wolfGray1, _, C.wolfGray1, C.wolfGray2, _, _, _],
  [_, _, C.wolfGray1, C.wolfGray2, _, C.wolfGray2, C.wolfGray1, _, _, _],
  [_, _, C.wolfGray2, C.wolfGray1, _, C.wolfGray1, C.wolfGray2, _, _, _],
  [_, _, C.wolfGray1, C.wolfGray2, _, C.wolfGray2, C.wolfGray1, _, _, _],
  [_, _, C.wolfGray3, C.wolfGray1, _, C.wolfGray1, C.wolfGray3, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const wolfBoots: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.wolfGray1, C.wolfGray2, _, C.wolfGray2, C.wolfGray1, _, _, _],
  [_, C.wolfGray2, C.wolfGray1, C.wolfWhite, _, C.wolfWhite, C.wolfGray1, C.wolfGray2, _, _],
  [_, C.wolfGray1, C.wolfGray3, C.wolfGray1, _, C.wolfGray1, C.wolfGray3, C.wolfGray1, _, _],
  [_, C.wolfGray2, C.wolfGray1, C.wolfGray2, _, C.wolfGray2, C.wolfGray1, C.wolfGray2, _, _],
  [_, C.wolfGray3, C.wolfGray2, C.wolfGray1, _, C.wolfGray1, C.wolfGray2, C.wolfGray3, _, _],
  [_, _, C.wolfGray1, C.wolfGray2, _, C.wolfGray2, C.wolfGray1, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const wolfGloves: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.wolfGray1, C.wolfGray2, _, C.wolfGray2, C.wolfGray1, _, _, _],
  [_, C.wolfGray2, C.wolfGray1, C.wolfWhite, _, C.wolfWhite, C.wolfGray1, C.wolfGray2, _, _],
  [_, C.wolfGray1, C.wolfGray3, C.wolfGray1, _, C.wolfGray1, C.wolfGray3, C.wolfGray1, _, _],
  [_, C.wolfGray2, C.wolfGray1, C.wolfGray2, _, C.wolfGray2, C.wolfGray1, C.wolfGray2, _, _],
  [_, _, C.wolfGray2, C.wolfGray1, _, C.wolfGray1, C.wolfGray2, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// ============ EPIC TIER - GOLEM ARMOR (purple glow) ============

const golemHelm: PixelGrid = [
  [_, _, _, C.golemGlow, C.golem2, C.golemGlow, _, _, _, _],
  [_, _, C.golem2, C.golemGlow, C.golem1, C.golemGlow, C.golem2, _, _, _],
  [_, C.golemGlow, C.golem1, C.golem2, C.golemGlow, C.golem2, C.golem1, C.golemGlow, _, _],
  [_, C.golem2, C.golem1, C.dark, C.dark, C.dark, C.golem1, C.golem2, _, _],
  [_, C.golem1, C.golemGlow, C.dark, _, C.dark, C.golemGlow, C.golem1, _, _],
  [_, _, C.golem2, C.golem1, C.golemGlow, C.golem1, C.golem2, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const golemChestplate: PixelGrid = [
  [_, _, _, C.golem1, C.golemGlow, C.golem1, _, _, _, _],
  [_, _, C.golem2, C.golemGlow, C.golem1, C.golemGlow, C.golem2, _, _, _],
  [_, C.golemGlow, C.golem1, C.golem2, C.golemGlow, C.golem2, C.golem1, C.golemGlow, _, _],
  [_, C.golem2, C.golem1, C.golemGlow, C.golem1, C.golemGlow, C.golem1, C.golem2, _, _],
  [_, C.golemGlow, C.golem2, C.golem1, C.golemGlow, C.golem1, C.golem2, C.golemGlow, _, _],
  [_, _, C.golem1, C.golem2, C.golem1, C.golem2, C.golem1, _, _, _],
  [_, _, C.golem2, C.golemGlow, C.golem2, C.golemGlow, C.golem2, _, _, _],
  [_, _, _, C.golem1, C.golem2, C.golem1, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const golemGreaves: PixelGrid = [
  [_, _, _, C.golem1, C.golemGlow, C.golem1, _, _, _, _],
  [_, _, C.golem2, C.golemGlow, C.golem1, C.golemGlow, C.golem2, _, _, _],
  [_, _, C.golemGlow, C.golem1, C.golem2, C.golem1, C.golemGlow, _, _, _],
  [_, _, C.golem2, C.golem1, _, C.golem1, C.golem2, _, _, _],
  [_, _, C.golemGlow, C.golem2, _, C.golem2, C.golemGlow, _, _, _],
  [_, _, C.golem2, C.golem1, _, C.golem1, C.golem2, _, _, _],
  [_, _, C.golem1, C.golemGlow, _, C.golemGlow, C.golem1, _, _, _],
  [_, _, C.golem2, C.golem1, _, C.golem1, C.golem2, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const golemGauntlets: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.golem1, C.golemGlow, _, C.golemGlow, C.golem1, _, _, _],
  [_, C.golem2, C.golemGlow, C.golem1, _, C.golem1, C.golemGlow, C.golem2, _, _],
  [_, C.golemGlow, C.golem1, C.golem2, _, C.golem2, C.golem1, C.golemGlow, _, _],
  [_, C.golem2, C.golem1, C.golemGlow, _, C.golemGlow, C.golem1, C.golem2, _, _],
  [_, _, C.golem2, C.golem1, _, C.golem1, C.golem2, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const golemBoots: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.golem1, C.golemGlow, _, C.golemGlow, C.golem1, _, _, _],
  [_, C.golem2, C.golemGlow, C.golem1, _, C.golem1, C.golemGlow, C.golem2, _, _],
  [_, C.golemGlow, C.golem1, C.golem2, _, C.golem2, C.golem1, C.golemGlow, _, _],
  [_, C.golem2, C.golem1, C.golemGlow, _, C.golemGlow, C.golem1, C.golem2, _, _],
  [_, C.golem1, C.golem2, C.golem1, _, C.golem1, C.golem2, C.golem1, _, _],
  [_, _, C.golem1, C.golem2, _, C.golem2, C.golem1, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// ============ EPIC TIER - DEMON ARMOR (red/black, fiery) ============

const demonCrown: PixelGrid = [
  [_, C.demon1, _, _, C.demonGlow, _, _, C.demon1, _, _],
  [_, C.demon2, C.demon1, _, C.demon1, _, C.demon1, C.demon2, _, _],
  [_, _, C.demon2, C.demon1, C.demonGlow, C.demon1, C.demon2, _, _, _],
  [_, C.demon1, C.demon2, C.demon3, C.demon1, C.demon3, C.demon2, C.demon1, _, _],
  [_, C.demon2, C.demon3, C.dark, C.dark, C.dark, C.demon3, C.demon2, _, _],
  [_, _, C.demon2, C.demon3, C.demonGlow, C.demon3, C.demon2, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const demonChestplate: PixelGrid = [
  [_, _, _, C.demon1, C.demonGlow, C.demon1, _, _, _, _],
  [_, _, C.demon2, C.demonGlow, C.demon1, C.demonGlow, C.demon2, _, _, _],
  [_, C.demon1, C.demon2, C.demon1, C.demonGlow, C.demon1, C.demon2, C.demon1, _, _],
  [_, C.demon2, C.demon1, C.demon3, C.demon1, C.demon3, C.demon1, C.demon2, _, _],
  [_, C.demonGlow, C.demon2, C.demon1, C.demonGlow, C.demon1, C.demon2, C.demonGlow, _, _],
  [_, _, C.demon1, C.demon2, C.demon1, C.demon2, C.demon1, _, _, _],
  [_, _, C.demon2, C.demonGlow, C.demon2, C.demonGlow, C.demon2, _, _, _],
  [_, _, _, C.demon3, C.demon1, C.demon3, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const demonGreaves: PixelGrid = [
  [_, _, _, C.demon1, C.demonGlow, C.demon1, _, _, _, _],
  [_, _, C.demon2, C.demonGlow, C.demon1, C.demonGlow, C.demon2, _, _, _],
  [_, _, C.demon1, C.demon3, C.demon1, C.demon3, C.demon1, _, _, _],
  [_, _, C.demonGlow, C.demon1, _, C.demon1, C.demonGlow, _, _, _],
  [_, _, C.demon1, C.demon2, _, C.demon2, C.demon1, _, _, _],
  [_, _, C.demon2, C.demonGlow, _, C.demonGlow, C.demon2, _, _, _],
  [_, _, C.demon1, C.demon2, _, C.demon2, C.demon1, _, _, _],
  [_, _, C.demon3, C.demon1, _, C.demon1, C.demon3, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const demonBoots: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.demon1, C.demonGlow, _, C.demonGlow, C.demon1, _, _, _],
  [_, C.demon2, C.demonGlow, C.demon1, _, C.demon1, C.demonGlow, C.demon2, _, _],
  [_, C.demon1, C.demon3, C.demon2, _, C.demon2, C.demon3, C.demon1, _, _],
  [_, C.demonGlow, C.demon1, C.demon2, _, C.demon2, C.demon1, C.demonGlow, _, _],
  [_, C.demon3, C.demon2, C.demon1, _, C.demon1, C.demon2, C.demon3, _, _],
  [_, _, C.demon1, C.demonGlow, _, C.demonGlow, C.demon1, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const demonGloves: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.demon1, C.demonGlow, _, C.demonGlow, C.demon1, _, _, _],
  [_, C.demon2, C.demonGlow, C.demon1, _, C.demon1, C.demonGlow, C.demon2, _, _],
  [_, C.demon1, C.demon3, C.demon2, _, C.demon2, C.demon3, C.demon1, _, _],
  [_, C.demonGlow, C.demon1, C.demon2, _, C.demon2, C.demon1, C.demonGlow, _, _],
  [_, _, C.demon2, C.demon1, _, C.demon1, C.demon2, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// ============ EPIC TIER - SPECTRAL ARMOR (ghostly purple/cyan) ============

const spectralHood: PixelGrid = [
  [_, _, _, C.spectral1, C.spectralGlow, C.spectral1, _, _, _, _],
  [_, _, C.spectral2, C.spectralGlow, C.spectral1, C.spectralGlow, C.spectral2, _, _, _],
  [_, C.spectral1, C.spectral2, C.spectral3, C.spectral3, C.spectral3, C.spectral2, C.spectral1, _, _],
  [_, C.spectral2, C.spectral3, _, C.spectralGlow, _, C.spectral3, C.spectral2, _, _],
  [_, C.spectralGlow, C.spectral3, _, _, _, C.spectral3, C.spectralGlow, _, _],
  [_, _, C.spectral2, C.spectral3, C.spectralGlow, C.spectral3, C.spectral2, _, _, _],
  [_, _, _, C.spectral1, C.spectral2, C.spectral1, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const spectralRobe: PixelGrid = [
  [_, _, _, C.spectral1, C.spectralGlow, C.spectral1, _, _, _, _],
  [_, _, C.spectral2, C.spectralGlow, C.spectral1, C.spectralGlow, C.spectral2, _, _, _],
  [_, C.spectralGlow, C.spectral1, C.spectral2, C.spectralGlow, C.spectral2, C.spectral1, C.spectralGlow, _, _],
  [_, C.spectral2, C.spectral1, C.spectral3, C.spectral1, C.spectral3, C.spectral1, C.spectral2, _, _],
  [_, C.spectral1, C.spectral2, C.spectralGlow, C.spectral2, C.spectralGlow, C.spectral2, C.spectral1, _, _],
  [_, _, C.spectralGlow, C.spectral2, C.spectral1, C.spectral2, C.spectralGlow, _, _, _],
  [_, _, C.spectral2, C.spectral1, C.spectralGlow, C.spectral1, C.spectral2, _, _, _],
  [_, _, _, C.spectral3, C.spectral2, C.spectral3, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const spectralLeggings: PixelGrid = [
  [_, _, _, C.spectral1, C.spectralGlow, C.spectral1, _, _, _, _],
  [_, _, C.spectral2, C.spectralGlow, C.spectral1, C.spectralGlow, C.spectral2, _, _, _],
  [_, _, C.spectralGlow, C.spectral3, C.spectral1, C.spectral3, C.spectralGlow, _, _, _],
  [_, _, C.spectral2, C.spectral1, _, C.spectral1, C.spectral2, _, _, _],
  [_, _, C.spectralGlow, C.spectral2, _, C.spectral2, C.spectralGlow, _, _, _],
  [_, _, C.spectral2, C.spectralGlow, _, C.spectralGlow, C.spectral2, _, _, _],
  [_, _, C.spectral1, C.spectral2, _, C.spectral2, C.spectral1, _, _, _],
  [_, _, C.spectral3, C.spectralGlow, _, C.spectralGlow, C.spectral3, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const spectralBoots: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.spectral1, C.spectralGlow, _, C.spectralGlow, C.spectral1, _, _, _],
  [_, C.spectral2, C.spectralGlow, C.spectral1, _, C.spectral1, C.spectralGlow, C.spectral2, _, _],
  [_, C.spectralGlow, C.spectral3, C.spectral2, _, C.spectral2, C.spectral3, C.spectralGlow, _, _],
  [_, C.spectral2, C.spectral1, C.spectralGlow, _, C.spectralGlow, C.spectral1, C.spectral2, _, _],
  [_, C.spectral3, C.spectral2, C.spectral1, _, C.spectral1, C.spectral2, C.spectral3, _, _],
  [_, _, C.spectralGlow, C.spectral2, _, C.spectral2, C.spectralGlow, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const spectralGloves: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.spectral1, C.spectralGlow, _, C.spectralGlow, C.spectral1, _, _, _],
  [_, C.spectral2, C.spectralGlow, C.spectral1, _, C.spectral1, C.spectralGlow, C.spectral2, _, _],
  [_, C.spectralGlow, C.spectral3, C.spectral2, _, C.spectral2, C.spectral3, C.spectralGlow, _, _],
  [_, C.spectral2, C.spectral1, C.spectralGlow, _, C.spectralGlow, C.spectral1, C.spectral2, _, _],
  [_, _, C.spectral2, C.spectral1, _, C.spectral1, C.spectral2, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const spectralAmulet: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.spectral3, C.spectral2, C.spectral3, C.spectral2, C.spectral3, _, _, _],
  [_, _, _, C.spectral2, C.spectralGlow, C.spectral2, _, _, _, _],
  [_, _, _, C.spectral3, C.spectralGlow, C.spectral3, _, _, _, _],
  [_, _, _, C.spectralGlow, C.white, C.spectralGlow, _, _, _, _],
  [_, _, _, C.spectral3, C.spectralGlow, C.spectral3, _, _, _, _],
  [_, _, _, _, C.spectral2, _, _, _, _, _],
  [_, _, _, _, C.spectral3, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// ============ ACCESSORIES ============

const warriorsRing: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, C.yellow1, C.yellow2, C.yellow1, _, _, _, _],
  [_, _, C.yellow2, C.yellow1, _, C.yellow1, C.yellow2, _, _, _],
  [_, _, C.yellow1, _, C.red1, _, C.yellow1, _, _, _],
  [_, _, C.yellow2, C.yellow1, _, C.yellow1, C.yellow2, _, _, _],
  [_, _, _, C.yellow1, C.yellow2, C.yellow1, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// ============ RANGED WEAPONS ============

// Primitive guns
const flintlockPistol: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, C.gunMetal, C.gunMetal2, C.gunMetal, _, _, _, _],
  [_, _, C.gunMetal2, C.gunMetal, C.gunMetal2, C.gunMetal, C.gunMetal, _, _, _],
  [_, C.brass, C.gunMetal, C.gunMetal2, C.gunMetal, C.gunMetal, C.gunMetal2, C.gunMetal, _, _],
  [_, _, C.gunMetal, C.gunMetal2, C.gunMetal, C.gunMetal, C.gunMetal2, _, _, _],
  [_, _, _, C.gunWood, C.gunWood2, C.gunWood, _, _, _, _],
  [_, _, _, C.gunWood2, C.gunWood, C.gunWood2, _, _, _, _],
  [_, _, _, _, C.gunWood, C.gunWood2, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const musket: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, C.gunMetal],
  [_, _, _, _, _, _, _, _, C.gunMetal2, C.gunMetal],
  [_, _, _, _, _, _, C.gunMetal, C.gunMetal2, C.gunMetal, _],
  [_, C.brass, C.gunMetal, C.gunMetal2, C.gunMetal, C.gunMetal2, C.gunMetal, _, _, _],
  [_, _, C.gunWood, C.gunWood2, C.gunWood, C.gunWood2, _, _, _, _],
  [_, _, _, C.gunWood, C.gunWood2, _, _, _, _, _],
  [_, _, _, _, C.gunWood, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const blunderbuss: PixelGrid = [
  [_, _, _, _, _, _, _, _, C.gunMetal2, C.gunMetal],
  [_, _, _, _, _, _, _, C.gunMetal, C.gunMetal2, C.gunMetal],
  [_, _, _, _, _, _, C.gunMetal2, C.gunMetal, C.gunMetal2, _],
  [_, C.brass, C.gunMetal, C.gunMetal2, C.gunMetal, C.gunMetal2, C.gunMetal, _, _, _],
  [_, _, C.gunWood, C.gunWood2, C.gunWood, C.gunWood2, _, _, _, _],
  [_, _, _, C.gunWood, C.gunWood2, C.gunWood, _, _, _, _],
  [_, _, _, _, C.gunWood, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// Modern guns
const pistol9mm: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, C.gunMetal, C.gunMetal2, C.gunMetal, _, _, _, _],
  [_, _, C.gunMetal2, C.gunBlack, C.gunMetal, C.gunBlack, C.gunMetal2, _, _, _],
  [_, _, C.gunMetal, C.gunBlack, C.gunMetal2, C.gunBlack, C.gunMetal, _, _, _],
  [_, _, _, C.gunBlack, C.gunMetal, C.gunBlack, _, _, _, _],
  [_, _, _, C.gunBlack, C.gunMetal2, C.gunBlack, _, _, _, _],
  [_, _, _, _, C.gunBlack, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const smg: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.gunMetal, C.gunMetal2, C.gunMetal, C.gunMetal2, C.gunMetal, _, _, _],
  [_, C.gunMetal2, C.gunBlack, C.gunMetal, C.gunBlack, C.gunMetal, C.gunBlack, C.gunMetal, _, _],
  [_, C.gunMetal, C.gunBlack, C.gunMetal2, C.gunBlack, C.gunMetal2, C.gunBlack, C.gunMetal, _, _],
  [_, _, C.gunBlack, C.gunMetal, C.gunBlack, C.gunMetal, C.gunBlack, _, _, _],
  [_, _, _, C.gunBlack, C.gunMetal2, C.gunBlack, _, _, _, _],
  [_, _, _, C.gunBlack, C.gunMetal, C.gunBlack, _, _, _, _],
  [_, _, _, _, C.gunBlack, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const assaultRifle: PixelGrid = [
  [_, _, _, _, _, _, _, _, C.gunMetal, _],
  [_, _, _, _, _, _, C.gunMetal, C.gunMetal2, C.gunMetal, _],
  [_, C.gunMetal, C.gunMetal2, C.gunBlack, C.gunMetal, C.gunMetal2, C.gunBlack, C.gunMetal, _, _],
  [_, C.gunMetal2, C.gunBlack, C.gunMetal, C.gunBlack, C.gunMetal, C.gunBlack, _, _, _],
  [_, _, C.gunBlack, C.gunMetal2, C.gunBlack, C.gunMetal2, _, _, _, _],
  [_, _, _, C.gunBlack, C.gunMetal, C.gunBlack, _, _, _, _],
  [_, _, _, C.gunBlack, C.gunMetal2, C.gunBlack, _, _, _, _],
  [_, _, _, _, C.gunBlack, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const shotgunPump: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, C.gunMetal],
  [_, _, _, _, _, _, _, C.gunMetal2, C.gunMetal, C.gunMetal2],
  [_, C.gunMetal, C.gunMetal2, C.gunBlack, C.gunMetal, C.gunMetal2, C.gunMetal, C.gunMetal2, _, _],
  [_, C.gunWood, C.gunWood2, C.gunBlack, C.gunMetal2, C.gunBlack, C.gunWood2, _, _, _],
  [_, _, C.gunWood, C.gunWood2, C.gunBlack, C.gunWood, _, _, _, _],
  [_, _, _, C.gunWood, C.gunWood2, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const sniperRifle: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, C.gunMetal],
  [_, _, _, _, _, C.gunMetal2, C.gunMetal, C.gunMetal2, C.gunMetal, C.gunMetal2],
  [_, _, C.gunMetal, C.blue1, C.blue2, C.gunMetal, C.gunMetal2, C.gunMetal, _, _],
  [_, C.gunMetal2, C.gunBlack, C.gunMetal, C.gunBlack, C.gunMetal, C.gunBlack, _, _, _],
  [_, _, C.gunBlack, C.gunMetal2, C.gunBlack, C.gunMetal2, _, _, _, _],
  [_, _, _, C.gunBlack, C.gunMetal, C.gunBlack, _, _, _, _],
  [_, _, _, _, C.gunBlack, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// Futuristic guns
const laserPistol: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, C.metal1, C.metal2, C.metal1, _, _, _, _],
  [_, _, C.metal2, C.laserRed, C.metal1, C.laserRed, C.metal2, _, _, _],
  [_, C.laserRed, C.metal1, C.laserRed, C.metal2, C.laserRed, C.metal1, _, _, _],
  [_, _, C.metal1, C.laserRed, C.metal1, C.laserRed, _, _, _, _],
  [_, _, _, C.metal2, C.metal1, C.metal2, _, _, _, _],
  [_, _, _, _, C.metal1, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const laserRifle: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, C.laserRed],
  [_, _, _, _, _, _, C.metal1, C.laserRed, C.metal2, C.laserRed],
  [_, C.metal1, C.metal2, C.laserRed, C.metal1, C.metal2, C.laserRed, C.metal1, _, _],
  [_, C.laserRed, C.metal1, C.metal2, C.laserRed, C.metal1, C.metal2, _, _, _],
  [_, _, C.metal2, C.laserRed, C.metal1, C.laserRed, _, _, _, _],
  [_, _, _, C.metal1, C.metal2, C.metal1, _, _, _, _],
  [_, _, _, _, C.metal1, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const pulseSmg: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, C.metal1, C.energyBlue, C.metal2, C.energyBlue, C.metal1, _, _, _],
  [_, C.energyBlue, C.metal2, C.energyBlue, C.metal1, C.energyBlue, C.metal2, C.metal1, _, _],
  [_, C.metal1, C.energyBlue, C.metal2, C.energyBlue, C.metal1, C.energyBlue, _, _, _],
  [_, _, C.metal2, C.energyBlue, C.metal1, C.energyBlue, _, _, _, _],
  [_, _, _, C.metal1, C.energyBlue, C.metal2, _, _, _, _],
  [_, _, _, C.metal2, C.metal1, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const plasmaRifle: PixelGrid = [
  [_, _, _, _, _, _, _, _, C.plasma1, _],
  [_, _, _, _, _, _, C.metal1, C.plasma2, C.plasma1, C.plasma2],
  [_, C.metal1, C.plasma1, C.metal2, C.plasma2, C.metal1, C.plasma1, C.metal2, _, _],
  [_, C.plasma2, C.metal1, C.plasma1, C.metal2, C.plasma1, C.metal1, _, _, _],
  [_, _, C.metal2, C.plasma2, C.metal1, C.plasma2, _, _, _, _],
  [_, _, _, C.metal1, C.metal2, C.metal1, _, _, _, _],
  [_, _, _, _, C.metal1, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const plasmaCannon: PixelGrid = [
  [_, _, _, _, _, _, _, C.plasma1, C.plasma2, C.plasma1],
  [_, _, _, _, _, C.plasma2, C.plasma1, C.plasma3, C.plasma1, C.plasma2],
  [_, C.metal1, C.metal2, C.plasma1, C.plasma2, C.plasma1, C.metal2, C.metal1, _, _],
  [_, C.plasma1, C.metal1, C.plasma2, C.plasma1, C.plasma2, C.metal1, _, _, _],
  [_, _, C.metal2, C.plasma1, C.metal2, C.plasma1, _, _, _, _],
  [_, _, _, C.metal1, C.metal2, C.metal1, _, _, _, _],
  [_, _, _, C.metal2, C.metal1, C.metal2, _, _, _, _],
  [_, _, _, _, C.metal1, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const railgun: PixelGrid = [
  [_, _, _, _, _, _, _, _, C.railGlow, C.railBlue],
  [_, _, _, _, _, _, C.railBlue, C.railGlow, C.railBlue, C.railGlow],
  [_, C.metal1, C.railBlue, C.railGlow, C.metal2, C.railGlow, C.railBlue, C.metal1, _, _],
  [_, C.railGlow, C.metal1, C.railBlue, C.railGlow, C.railBlue, C.metal2, _, _, _],
  [_, _, C.metal2, C.railGlow, C.metal1, C.railGlow, _, _, _, _],
  [_, _, _, C.metal1, C.railBlue, C.metal1, _, _, _, _],
  [_, _, _, C.metal2, C.metal1, C.metal2, _, _, _, _],
  [_, _, _, _, C.metal1, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// ============ AMMUNITION ============

const musketBall: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, C.gunMetal2, C.gunMetal, _, _, _, _],
  [_, _, _, C.gunMetal, C.gunMetal2, C.gunMetal, C.gunMetal2, _, _, _],
  [_, _, _, C.gunMetal2, C.gunMetal, C.metal1, C.gunMetal, _, _, _],
  [_, _, _, C.gunMetal, C.gunMetal2, C.gunMetal, C.gunMetal2, _, _, _],
  [_, _, _, _, C.gunMetal, C.gunMetal2, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const buckshot: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, C.gunMetal, _, C.gunMetal, _, _, _, _],
  [_, _, C.gunMetal, C.gunMetal2, _, C.gunMetal2, C.gunMetal, _, _, _],
  [_, _, _, _, C.gunMetal, _, _, _, _, _],
  [_, _, C.gunMetal, C.gunMetal2, _, C.gunMetal2, C.gunMetal, _, _, _],
  [_, _, _, C.gunMetal, _, C.gunMetal, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const pistolAmmo: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, C.brass, _, _, _, _, _],
  [_, _, _, C.brass, C.brass2, C.brass, _, _, _, _],
  [_, _, C.brass, C.brass2, C.gunMetal, C.brass2, C.brass, _, _, _],
  [_, _, C.brass, C.gunMetal, C.gunMetal2, C.gunMetal, C.brass, _, _, _],
  [_, _, C.brass, C.brass2, C.gunMetal, C.brass2, C.brass, _, _, _],
  [_, _, _, C.brass, C.brass2, C.brass, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const rifleAmmo: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, C.gunMetal, _, _, _, _, _],
  [_, _, _, C.gunMetal, C.gunMetal2, C.gunMetal, _, _, _, _],
  [_, _, C.brass, C.gunMetal, C.gunMetal2, C.gunMetal, C.brass, _, _, _],
  [_, _, C.brass, C.brass2, C.gunMetal, C.brass2, C.brass, _, _, _],
  [_, _, C.brass, C.brass2, C.brass, C.brass2, C.brass, _, _, _],
  [_, _, _, C.brass, C.brass2, C.brass, _, _, _, _],
  [_, _, _, _, C.brass, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const shotgunShells: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, C.brass, C.brass2, _, _, _, _],
  [_, _, _, C.brass, C.brass2, C.brass, _, _, _, _],
  [_, _, C.red1, C.red2, C.brass, C.red2, C.red1, _, _, _],
  [_, _, C.red2, C.red1, C.red2, C.red1, C.red2, _, _, _],
  [_, _, C.red1, C.red2, C.red1, C.red2, C.red1, _, _, _],
  [_, _, _, C.red2, C.red1, C.red2, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const energyCell: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, C.metal1, C.metal2, C.metal1, _, _, _, _],
  [_, _, C.metal2, C.energyBlue, C.energyBlue2, C.energyBlue, C.metal2, _, _, _],
  [_, _, C.metal1, C.energyBlue2, C.white, C.energyBlue2, C.metal1, _, _, _],
  [_, _, C.metal2, C.energyBlue, C.energyBlue2, C.energyBlue, C.metal2, _, _, _],
  [_, _, C.metal1, C.energyBlue2, C.white, C.energyBlue2, C.metal1, _, _, _],
  [_, _, _, C.metal2, C.metal1, C.metal2, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const plasmaCell: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, C.metal1, C.metal2, C.metal1, _, _, _, _],
  [_, _, C.metal2, C.plasma1, C.plasma2, C.plasma1, C.metal2, _, _, _],
  [_, _, C.metal1, C.plasma2, C.white, C.plasma2, C.metal1, _, _, _],
  [_, _, C.metal2, C.plasma1, C.plasma2, C.plasma1, C.metal2, _, _, _],
  [_, _, C.metal1, C.plasma2, C.white, C.plasma2, C.metal1, _, _, _],
  [_, _, _, C.metal2, C.metal1, C.metal2, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

const railCharge: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, C.metal1, C.railGlow, C.metal1, _, _, _, _],
  [_, _, C.metal2, C.railBlue, C.railGlow, C.railBlue, C.metal2, _, _, _],
  [_, _, C.railGlow, C.railBlue, C.white, C.railBlue, C.railGlow, _, _, _],
  [_, _, C.metal2, C.railGlow, C.railBlue, C.railGlow, C.metal2, _, _, _],
  [_, _, C.railGlow, C.railBlue, C.white, C.railBlue, C.railGlow, _, _, _],
  [_, _, _, C.metal2, C.railGlow, C.metal2, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _],
];

// Map item IDs to icons
const iconMap: Record<string, PixelGrid> = {
  // Tools
  axe_wood: axeWood,
  axe_stone: axeStone,
  axe_iron: axeIron,
  pickaxe_wood: pickaxeWood,
  pickaxe_stone: pickaxeStone,
  pickaxe_iron: pickaxeIron,
  sickle_wood: sickleWood,
  sickle_stone: sickleStone,
  torch: torch,
  jackdrill: jackdrill,

  // Weapons
  sword_wood: swordWood,
  sword_stone: swordStone,
  sword_iron: swordIron,
  spear_wood: spearWood,
  spear_stone: spearStone,
  bone_knife: boneKnife,
  bone_spear: boneSpear,
  golem_sword: golemSword,
  wolf_fang_dagger: wolfFangDagger,
  bone_club: boneClub,
  orc_cleaver: orcCleaver,
  golem_hammer: golemHammer,
  demon_blade: demonBlade,
  spectral_scythe: spectralScythe,
  spider_fang: spiderFang,

  // Resources
  wood: wood,
  stone: stone,
  fiber: fiber,
  iron_ore: ironOre,
  coal: coal,

  // Food
  apple: apple,
  raw_meat: rawMeat,
  cooked_meat: cookedMeat,

  // Monster drops
  bone: bone,
  ectoplasm: ectoplasm,
  spider_eye: spiderEye,
  wolf_pelt: wolfPelt,
  golem_core: golemCore,
  demon_heart: demonHeart,

  // Consumables
  bandage: bandage,
  health_potion: healthPotion,
  antidote: antidote,
  fur_cloak: furCloak,
  spirit_essence: spiritEssence,

  // Regular tier - Cloth armor
  cloth_hood: clothHood,
  cloth_shirt: clothShirt,
  cloth_pants: clothPants,
  cloth_sandals: clothSandals,
  cloth_wraps: clothWraps,

  // Regular tier - Leather armor
  leather_cap: leatherCap,
  leather_tunic: leatherTunic,
  leather_pants: leatherPants,
  leather_boots: leatherBoots,
  leather_gloves: leatherGloves,

  // Magic tier - Iron armor
  iron_helm: ironHelm,
  iron_chestplate: ironChestplate,
  iron_greaves: ironGreaves,
  iron_boots: ironBoots,

  // Magic tier - Bone armor
  bone_helm: boneHelm,
  bone_armor: boneArmor,
  bone_greaves: boneGreaves,
  bone_boots: boneBoots,

  // Magic tier - Wolf armor
  wolf_hood: wolfHood,
  wolf_pelt_cloak: wolfPeltCloak,
  wolf_pants: wolfPants,
  wolf_boots: wolfBoots,
  wolf_gloves: wolfGloves,

  // Epic tier - Golem armor
  golem_helm: golemHelm,
  golem_chestplate: golemChestplate,
  golem_greaves: golemGreaves,
  golem_gauntlets: golemGauntlets,
  golem_boots: golemBoots,

  // Epic tier - Demon armor
  demon_crown: demonCrown,
  demon_chestplate: demonChestplate,
  demon_greaves: demonGreaves,
  demon_boots: demonBoots,
  demon_gloves: demonGloves,

  // Epic tier - Spectral armor
  spectral_hood: spectralHood,
  spectral_robe: spectralRobe,
  spectral_leggings: spectralLeggings,
  spectral_boots: spectralBoots,
  spectral_gloves: spectralGloves,
  spectral_amulet: spectralAmulet,

  // Accessories
  warriors_ring: warriorsRing,

  // Ranged Weapons - Primitive
  flintlock_pistol: flintlockPistol,
  musket: musket,
  blunderbuss: blunderbuss,

  // Ranged Weapons - Modern
  pistol_9mm: pistol9mm,
  smg: smg,
  assault_rifle: assaultRifle,
  shotgun_pump: shotgunPump,
  sniper_rifle: sniperRifle,

  // Ranged Weapons - Futuristic
  laser_pistol: laserPistol,
  laser_rifle: laserRifle,
  pulse_smg: pulseSmg,
  plasma_rifle: plasmaRifle,
  plasma_cannon: plasmaCannon,
  railgun: railgun,

  // Ammunition
  musket_ball: musketBall,
  buckshot: buckshot,
  pistol_ammo: pistolAmmo,
  rifle_ammo: rifleAmmo,
  shotgun_shells: shotgunShells,
  energy_cell: energyCell,
  plasma_cell: plasmaCell,
  rail_charge: railCharge,
};

/**
 * Render a pixel art icon at the specified position
 */
export function renderItemIcon(
  ctx: CanvasRenderingContext2D,
  itemId: string,
  x: number,
  y: number,
  size: number
): boolean {
  const icon = iconMap[itemId];
  if (!icon) return false;

  const pixelSize = size / 10;

  for (let row = 0; row < 10; row++) {
    for (let col = 0; col < 10; col++) {
      const color = icon[row]?.[col];
      if (color) {
        ctx.fillStyle = color;
        ctx.fillRect(
          x + col * pixelSize,
          y + row * pixelSize,
          pixelSize + 0.5, // Slight overlap to prevent gaps
          pixelSize + 0.5
        );
      }
    }
  }

  return true;
}

/**
 * Check if an item has a pixel art icon
 */
export function hasItemIcon(itemId: string): boolean {
  return itemId in iconMap;
}
